import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MediaGallery from './MediaGallery.js';
import './Modal.css';

const Modal = ({ isOpen, onClose, experience }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !experience) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-content"
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="close" onClick={onClose}>&times;</span>

          {experience.hero ? (
            <div className="modal-hero">
              <img
                src={experience.hero}
                alt={`${experience.company} banner`}
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
            </div>
          ) : null}

          <div className="modal-header">
            <div className="modal-title-row">
              <h2>{experience.title}</h2>
            </div>
            <div className="modal-meta">
              {(() => {
                const getArchiveUrl = (exp) => {
                  const url = exp.website || '';
                  if (!url) return '';
                  const period = exp.period || '';
                  const years = (period.match(/\d{4}/g) || []).map(Number);
                  let year = years[0] || new Date().getFullYear();
                  if (years.length >= 2) {
                    const end = years[1];
                    year = Math.floor((years[0] + end) / 2);
                  }
                  return `https://web.archive.org/web/${year}0101/${url}`;
                };
                const href = getArchiveUrl(experience);
                return href ? (
                  <a className="modal-company" href={href} target="_blank" rel="noopener noreferrer" title="View via Internet Archive">
                    {experience.company}
                  </a>
                ) : (
                  <span className="modal-company">{experience.company}</span>
                );
              })()}
              <span className="modal-period">{experience.period}</span>
              {experience.location && (
                <span className="modal-location">
                  <i className="fas fa-map-marker-alt"></i>
                  {experience.location}
                </span>
              )}
            </div>
          </div>

          <div className="modal-description">
            <h3>Overview</h3>
            <p>{experience.description}</p>
          </div>
          
          <div className="modal-technologies">
            <h3>Technologies Used</h3>
            <div className="tech-stack">
              {experience.technologies.map((tech, index) => (
                <span key={index} className="tech">{tech}</span>
              ))}
            </div>
          </div>
          
          <div className="modal-achievements">
            <h3>Key Achievements</h3>
            <ul>
              {experience.achievements.map((achievement, index) => (
                <li key={index}>{achievement}</li>
              ))}
            </ul>
          </div>
          
          <div className="modal-impact">
            <h3>Impact</h3>
            <p>{experience.impact}</p>
          </div>

          {(experience.media.images.length > 0 || experience.media.videos.length > 0) && (
            <div className="modal-media">
              <h3>Media</h3>
              <MediaGallery media={experience.media} />
            </div>
          )}

          {experience.logo ? (
            <div className="modal-company-logo">
              <img
                className="modal-logo"
                src={experience.logo}
                alt={`${experience.company} logo`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          ) : null}

          {experience.heroLocal ? (
            <div className="modal-hero-bottom">
              <img
                src={experience.heroLocal}
                alt={`${experience.company} local banner`}
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
            </div>
          ) : null}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Modal;
